import type { CguType } from "@/features/cgu/types";
import {
  findMissingProfileKeysSignup,
  REQUIRED_SUBSTEPS_AT_CONNECTION,
  REQUIRED_SUBSTEPS_AT_CONNECTION_LV2
} from "@/features/flow/constants";
import { useCompleteAccount } from "@/features/flow/hooks/use-complete-account.ts";
import { useCreateAccount } from "@/features/flow/hooks/use-create-account";
import { useCreateAccountMirror } from "@/features/flow/hooks/use-create-account-mirror";
import { useManipulatePartner } from "@/features/flow/hooks/use-manipulate-partner";
import { asSubstep, DeclarativeIdentity, DeclarativePerson } from "@/features/flow/types";
import { getNextSubstep } from "@/features/register/utils";
import { Routes } from "@/features/router";
import { useUser } from "@/features/user/hooks/use-user";
import { NaturalPerson, UserType } from "@/features/user/types.ts";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { StorageService } from "@/features/utils/storage";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setIsLoading } from "@/redux/slices/flowSlice";
import { setSubstepNamesOfMissingFields } from "@/redux/slices/missingFieldsSlice";
import {
  getOtpToken,
  setDeclarativeIdentity,
  setPassword,
  setPhone,
  setwithPhone,
  SIGNUP_SLICE_INITIAL
} from "@/redux/slices/signupSlice";
import { getRequiredConnectionKeysFlow } from "@/utils/declarative.utils";
import { mapNpToSignupState, mapUserTypeToDeclarativeIdentity } from "@/utils/mappers.utils";
import { getRoute, OlkyPassRoutes } from "@/utils/routes";
import { client } from "@passwordless-id/webauthn";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { animatedNext } from "../../layout/AnimatedForm.react";
import { AccountSecurityPassword } from "./account-security-password/account-security-password.react";
import { AccountSecurityWebauthn } from "./account-security-webauthn/account-security-webauthn.react";
import CguMissing from "./cgu";
import { CreatingAccount } from "./creating-account/index.react";
import DeclarativeIdentityRoot from "./declarative-identity/index.react";
import EnterPhone from "./enter-phone/EnterPhone.react";
import VerifyEmail from "./verify-email/VerifyEmail.react";
import { Gender } from "@/features/auth/types";

type SignupRootProps = {
  input: SignupInput;
  cguListData: CguType[];
};

export type SignupInput = {
  email: string;
};

function SignupRoot({ input, cguListData }: SignupRootProps) {
  const [firstSubstep, setFirstSubstep] = useState<string | undefined>(undefined);
  const { locale } = useLocale();
  const { useCountries, useUserProfile } = useUser();
  const { refetch } = useUserProfile();
  const { t } = useTranslation(["pass"]);

  const { data: countries } = useCountries(locale.language);
  const isInPreviousStep = useAppSelector((state) => state.flow.isInPreviousStep);
  const substepsOfMissingfields = useAppSelector((state) => state.missingFields.substeps);
  const { block, session_uuid, step, substep } = useParams();
  const navigate = useNavigate();
  const [loadingCheck, setLoadingCheck] = useState<boolean>(false);
  const withPhone = useAppSelector((state) => state.signup.withPhone);

  const signupState = useAppSelector((state) => state.signup);
  const dispatch = useAppDispatch();
  const jwt_otp = useAppSelector(getOtpToken);
  const mirror_uuid = useAppSelector((state) => state.signup.mirror_uuid);

  const tempUuid = sessionStorage.getItem("temp_uuid_real");
  const [profile, setProfile] = useState<NaturalPerson | null>(null);
  const [missingKeys, setMissingKeys] = useState({});

  // Right now isPendingCreateAccount from react-query is not working, this is a workaround
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const { mutateAsync: createAccount } = useCreateAccount();
  const { mutateAsync: createAccountMirror } = useCreateAccountMirror();

  const { mutateAsync: completeMissingAccountInfos } = useCompleteAccount();
  const initialMissingFields = useAppSelector((state) => state.missingFields.initialMissingFields);
  const olkypassLevel = useAppSelector((state) => state.olkypass.level);
  const [cguRequired, setCguRequired] = useState<CguType | null>(null);
  const isLoading = useAppSelector((state) => state.flow.isLoading);
  const isLaymoonFlowGoingBackFromPasswordStep = useAppSelector(
    (state) => state.signup.isLaymoonFlowGoingBackFromPasswordStep
  );
  const manipulatePartner = useManipulatePartner();
  const storedValue = sessionStorage.getItem("NewOlkyPayUser");

  const setLoading = (loading: boolean) => {
    if (block === "required_field" && mirror_uuid === null && tempUuid === null)
      dispatch(setIsLoading(loading));
    else setIsCreatingAccount(loading);
  };
  const handleMissing = (profilUser: UserType) => {
    const missingKeys = findMissingProfileKeysSignup(profilUser.naturalPerson);
    const missingKeysObj = Object.keys(missingKeys);

    if (missingKeysObj.length > 0) {
      {
        sessionStorage.setItem(
          "NewOlkyPayUser",
          JSON.stringify({ profile: profilUser, missingKeys: missingKeys })
        );
        const withPhone = "phone.phone" in missingKeys;
        const first_step = "phone.phone" in missingKeys ? "phone" : "identity";
        dispatch(setwithPhone(withPhone));
        dispatch(setSubstepNamesOfMissingFields(missingKeysObj));
        navigate(
          getRoute(Routes.flow_step, {
            session_uuid,
            block: "required_field",
            step: first_step
          })
        );
      }
    }
  };
  useEffect(() => {
    if (storedValue) {
      //if we have stored values coming from /profile
      const jsonItems = JSON.parse(storedValue);
      setProfile(jsonItems.profile.naturalPerson);
      const missingFields = jsonItems.missingKeys;

      if (block === "required_field" && signupState === SIGNUP_SLICE_INITIAL) {
        //we initialize the signupState with values coming from /profile
        //only for "required_field" flow
        const nationalityCode = countries?.countries.find(
          (country) =>
            country.id.toString() ===
            jsonItems.profile.naturalPerson.CountryOfCitizenship?.toString()
        )?.alpha2;
        const initialSignupState = mapNpToSignupState(
          jsonItems.profile.naturalPerson,
          nationalityCode ? [nationalityCode] : []
        );
        if (initialSignupState.identity) {
          const substeps =
            olkypassLevel > 1
              ? REQUIRED_SUBSTEPS_AT_CONNECTION_LV2
              : REQUIRED_SUBSTEPS_AT_CONNECTION;
          //here we need to initialize missing fields
          for (const missingFieldSubstep of Object.keys(missingFields)) {
            if (!substeps.includes(missingFieldSubstep)) {
              delete missingFields[missingFieldSubstep];
            }
          }
          dispatch(setSubstepNamesOfMissingFields(Object.keys(missingFields)));
          setMissingKeys(missingFields);
          dispatch(setDeclarativeIdentity(initialSignupState.identity));
        }
      } else {
        //signup flow
        setMissingKeys(jsonItems.missingKeys);
      }
    }
  }, [storedValue]);
  useEffect(() => {
    if (!session_uuid || !step) {
      navigate(getRoute(Routes.flow_step, { session_uuid, block, step: "verify_email" }), {
        replace: true
      });
    }
  }, [session_uuid, step]);

  const completeInfo = async () => {
    setLoading(true);
    if (mirror_uuid || tempUuid) return;
    try {
      if (!isLoading)
        await completeMissingAccountInfos({
          signupState: {
            ...signupState,
            security: { isWebauthn: false, password: "" }
          },
          email: input.email,
          password: "",
          initialMissingKeys: initialMissingFields
        });
    } catch (e) {
      console.log("Error occurred while completing account info:", e);
    } finally {
      await manipulatePartner();
      await refetch();
    }
  };

  useEffect(() => {
    if (block !== "required_field" && !isCreatingAccount) {
      //signup flow
      if (
        profile &&
        ((Object.keys(missingKeys).length == 1 &&
          Object.hasOwn(missingKeys, "phone.phone") &&
          signupState.phone) ||
          (Object.keys(missingKeys).length >= 1 && signupState.identity && mirror_uuid === null))
      ) {
        // Check if identity is set before calling
        completeInfo();
      }
    } else if (profile && signupState !== SIGNUP_SLICE_INITIAL) {
      const updatedMissingKeys = getRequiredConnectionKeysFlow(
        countries?.countries ?? [],
        signupState,
        profile,
        olkypassLevel
      );
      setMissingKeys(updatedMissingKeys);
      //submit only if we clicked "next" in the last substep
      if (
        Object.keys(updatedMissingKeys).length === 0 &&
        !isInPreviousStep &&
        block &&
        getNextSubstep(block, substepsOfMissingfields, asSubstep(substep)) === null &&
        mirror_uuid === null &&
        tempUuid === null
      )
        completeInfo().then(async () => navigate(OlkyPassRoutes.dashboard));
    }
  }, [signupState, input.email]);

  //After submitting the phone we go to the "identity" step
  //it's why we need to calculate the first substep
  //("missing fields" flow)
  const firstsubstep = () => {
    if (isLaymoonFlowGoingBackFromPasswordStep) {
      return "source_income";
    }
    return firstSubstep;
  };
  const firstIdentitySubstep =
    block === "required_field"
      ? getNextSubstep("required_field", substepsOfMissingfields, asSubstep("phone"))
      : undefined;

  if (isCreatingAccount) {
    return <CreatingAccount />;
  }

  return (
    <>
      {step === "verify_email" && (
        <VerifyEmail
          input={{ email: input.email }}
          mirror_uuid={mirror_uuid ?? null}
          onValidated={(data?: UserType) =>
            animatedNext("forward", async () => {
              const identity: DeclarativeIdentity = mapUserTypeToDeclarativeIdentity(
                data,
                countries?.countries
              );
              dispatch(setDeclarativeIdentity(identity));
              if (data?.identityPartner && data.identityPartner.force_webauthn) {
                if (!client.isAvailable()) {
                  toast.error(t("flow.id_control.error.webauthn_not_available"));
                  return;
                }

                sessionStorage.setItem(
                  "force_webauthn",
                  JSON.stringify(data.identityPartner.force_webauthn)
                );
              }
              if ((mirror_uuid || tempUuid) && data) handleMissing(data);
              else
                navigate(
                  getRoute(Routes.flow_step, {
                    session_uuid,
                    block,
                    step: block === "required_field" ? "identity" : "phone"
                  })
                );
            })
          }
        />
      )}

      {step === "phone" && (
        <EnterPhone
          onValidated={(output) =>
            animatedNext("forward", async () => {
              dispatch(
                setPhone({
                  phone: output.phone,
                  cguItem: output.cguItem
                })
              );
              setFirstSubstep(firstIdentitySubstep ?? undefined);
              navigate(
                getRoute(Routes.flow_step, {
                  session_uuid,
                  block,
                  step: "identity"
                })
              );
            })
          }
          cguList={cguListData as CguType[]}
        />
      )}

      {step === "identity" && (
        <DeclarativeIdentityRoot
          countries={countries?.countries ?? []}
          input={{
            email: input.email,
            phone: signupState.phone?.phone ?? ""
          }}
          firstSubstep={firstsubstep()} //for "missing fields" flow
          onValidated={async (declarativeIdentity) => {
            if (block === "required_field" && mirror_uuid === null && tempUuid === null) {
              await manipulatePartner();
            } else {
              animatedNext("forward", async () => {
                dispatch(setDeclarativeIdentity(declarativeIdentity));
                if (!(profile && Object.keys(missingKeys).length > 0) || mirror_uuid || tempUuid) {
                  if (!withPhone && (mirror_uuid || tempUuid))
                    navigate(
                      getRoute(Routes.flow_step, {
                        session_uuid,
                        block,
                        step: "cguMirror"
                      })
                    );
                  else
                    navigate(
                      getRoute(Routes.flow_step, {
                        session_uuid,
                        block,
                        step: "security"
                      })
                    );
                }
              });
            }
          }}
        />
      )}
      {step === "cguMirror" && (
        <CguMissing
          loadingCheck={loadingCheck}
          onValidated={async (item) => {
            setCguRequired(item);
            setLoadingCheck(true);
            if (mirror_uuid || tempUuid) {
              navigate(
                getRoute(Routes.flow_step, {
                  session_uuid,
                  block,
                  step: "security"
                })
              );
            } else await manipulatePartner();
          }}
          cguList={cguListData ?? []}
        />
      )}
      {step === "security" && (
        <AccountSecurityPassword
          onValidated={async (output) =>
            animatedNext("forward", async () => {
              (async () => {
                setLoading(true);
                StorageService.setItem("jwt", {});
                try {
                  const profil = JSON.parse(sessionStorage.getItem("NewOlkyPayUser") as string)
                    ?.profile as UserType;
                  if (mirror_uuid || tempUuid) {
                    const address = profil?.naturalPerson.Address.find(
                      (ad) => ad.isPrincipalAddress
                    );
                    const getCountryAlpha2ById = (id: number | null) => {
                      if (!id) return null;
                      return (countries?.countries ?? []).find((country) => country.id == id)
                        ?.alpha2;
                    };

                    await createAccountMirror({
                      signupState: {
                        ...signupState,
                        cguItem: !withPhone ? cguRequired : signupState.cguItem,
                        identity: {
                          ...signupState.identity,

                          addressBirth: {
                            birth:
                              signupState.identity?.addressBirth?.birth ??
                              new Date(profil.naturalPerson.DateOfBirth.date),
                            cityOfBirth:
                              signupState.identity?.addressBirth?.cityOfBirth ??
                              profil.naturalPerson.Birthplace,
                            countryCode:
                              signupState.identity?.addressBirth?.countryCode ??
                              profil.naturalPerson.countryOfBirth.toString(),
                            zip: signupState.identity?.addressBirth?.zip ?? ""
                          },
                          addressResidence: {
                            ...signupState.identity?.addressResidence,
                            addressLine:
                              signupState.identity?.addressResidence?.addressLine ??
                              address?.Address1,
                            city: signupState.identity?.addressResidence?.city ?? address?.City,
                            countryCode:
                              signupState.identity?.addressResidence?.countryCode ??
                              address?.Country,
                            zip: signupState.identity?.addressResidence?.zip ?? address?.ZipCode
                          },
                          monthlyIncome: {
                            maximum:
                              signupState.identity?.monthlyIncome?.maximum ??
                              profil.naturalPerson.maximumMonthlyIncome,
                            minimum:
                              signupState.identity?.monthlyIncome?.minimum ??
                              profil.naturalPerson.minimumMonthlyIncome,
                            trans_key: signupState.identity?.monthlyIncome?.trans_key as string
                          },
                          occupation: {
                            activity:
                              (signupState.identity?.occupation?.activity as string) ??
                              profil.naturalPerson?.ActivityCode?.toString() ??
                              null,
                            CSPcode:
                              signupState.identity?.occupation?.CSPcode ??
                              profil.naturalPerson.CSPCode,
                            description:
                              signupState.identity?.occupation?.description ??
                              profil.naturalPerson.activityDescription ??
                              null
                          },

                          person: {
                            ...(signupState.identity?.person as DeclarativePerson),
                            firstnames:
                              signupState.identity?.person?.firstnames ??
                              profil.naturalPerson.FirstName,
                            lastnames:
                              signupState.identity?.person?.lastnames ??
                              profil.naturalPerson.LastName,
                            otherfirstnames:
                              signupState.identity?.person?.otherfirstnames ??
                              profil.naturalPerson.OtherFirstNames,
                            gender:
                              (signupState.identity?.person?.gender ??
                              profil.naturalPerson.Gender === "Mr")
                                ? Gender.Man
                                : Gender.Woman,
                            nationalityCodes:
                              signupState.identity?.person?.nationalityCodes &&
                              signupState.identity?.person?.nationalityCodes?.length > 0
                                ? (signupState.identity?.person?.nationalityCodes as string[])
                                : ([
                                    getCountryAlpha2ById(profil.naturalPerson.CountryOfCitizenship),
                                    getCountryAlpha2ById(
                                      profil.naturalPerson.CountryOfCitizenship2
                                    ),
                                    getCountryAlpha2ById(
                                      profil.naturalPerson.CountryOfCitizenship3
                                    ),
                                    getCountryAlpha2ById(profil.naturalPerson.CountryOfCitizenship4)
                                  ] as string[])
                          },
                          sourcesIncome:
                            signupState.identity?.sourcesIncome &&
                            signupState.identity?.sourcesIncome.length > 0
                              ? signupState.identity?.sourcesIncome
                              : profil.naturalPerson.sourcesIncome,
                          taxAdress:
                            signupState.identity?.taxAdress &&
                            signupState.identity?.taxAdress.length > 0
                              ? signupState.identity?.taxAdress
                              : profil.naturalPerson.TaxAddress,
                          welcome: signupState.identity?.welcome
                        },
                        jwt_otp: jwt_otp,
                        security: { isWebauthn: false, password: output.password }
                      },
                      email: input.email,
                      password: output.password ?? ""
                    });
                  } else
                    await createAccount({
                      signupState: {
                        ...signupState,
                        jwt_otp: jwt_otp,
                        security: { isWebauthn: false, password: output.password }
                      },
                      email: input.email,
                      password: output.password ?? ""
                    });
                } catch (error) {
                  if (
                    error instanceof Error &&
                    error.message &&
                    error.message === "Invalid OTP JWT."
                  ) {
                    toast.error(t("flow.id_control.error.invalid_jwt"));
                    navigate(getRoute(Routes.flow + "/-/welcome"));
                    return;
                  }
                }
                setLoading(false);
                StorageService.setItem("username", input.email);

                if (!client.isAvailable()) {
                  animatedNext("forward", async () => await manipulatePartner());
                  return;
                }
                dispatch(setPassword(output.password));

                navigate(
                  getRoute(Routes.flow_step, {
                    session_uuid,
                    block,
                    step: "webauthn"
                  })
                );
              })();
            })
          }
        />
      )}
      {step === "webauthn" && (
        <AccountSecurityWebauthn
          force_webauthn={
            sessionStorage.getItem("force_webauthn") !== null
              ? JSON.parse(sessionStorage.getItem("force_webauthn") as string)
              : false
          }
          onValidated={async () => await manipulatePartner()}
        />
      )}
    </>
  );
}

export default SignupRoot;
